// Safe parser for Supabase responses. Validates with zod, throws on
// drift. Use for every `.select('*')` / `.rpc()` result so the domain
// layer is always strictly typed.

import type { z, ZodTypeAny } from "zod";

export class DbParseError extends Error {
  constructor(
    public readonly schemaName: string,
    public readonly issues: ReadonlyArray<{ path: string; message: string }>,
    public readonly raw: unknown,
  ) {
    super(`[db-schema] ${schemaName} validation failed: ${JSON.stringify(issues)}`);
    this.name = "DbParseError";
  }
}

export function parseRow<S extends ZodTypeAny>(schema: S, raw: unknown, name?: string): z.infer<S> {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues.map((i) => ({ path: i.path.join("."), message: i.message }));
    throw new DbParseError(name ?? schema.description ?? "row", issues, raw);
  }
  return result.data as z.infer<S>;
}

export function parseRows<S extends ZodTypeAny>(schema: S, raw: unknown, name?: string): ReadonlyArray<z.infer<S>> {
  if (!Array.isArray(raw)) {
    throw new DbParseError(name ?? schema.description ?? "rows", [{ path: "_", message: "expected array" }], raw);
  }
  return raw.map((row) => parseRow(schema, row, name));
}

export function tryParseRow<S extends ZodTypeAny>(schema: S, raw: unknown, name?: string): z.infer<S> | null {
  if (raw === null || raw === undefined) return null;
  const result = schema.safeParse(raw);
  if (!result.success) {
    if (import.meta.env.DEV) {
      console.warn(`[db-schema] ${name ?? "row"} validation failed:`, result.error.issues);
    }
    return null;
  }
  return result.data as z.infer<S>;
}
